/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import i18n from 'i18next';

import { Enrollments } from '@/videosEnrollments';
import { InsightsDashboardsContainer } from '@/insights';
import { urlSearch } from '@/search';
import { VersioningList } from '@/exceed';

import VideosAdminComposeContainer from '../containers/videosAdminComposeContainer';
import VideosAdminContentContainer from '../containers/videosAdminContentContainer';
import VideosAdminInfo from '../components/videosAdminInfo';
import VideosAdminPropertiesContainer from '../containers/videosAdminPropertiesContainer';
import VideosAdminViews from '../components/videosAdminViews';
import VideosAdminPermissionsContainer from '../containers/videosAdminPermissionsContainer';
import VideosAdminTopicPropertiesContainer from '../containers/videosAdminTopicPropertiesContainer';
import VideosUnauthorizedContainer from '../containers/videosUnauthorizedContainer';

import InsightsTab from '../components/insightsTab';

import getAxiosConfig from './getAxiosConfig';
import getVideoAdminEnrollmentsOptions from './getVideoAdminEnrollmentsOptions';
import getVideosVersioningComposeSchema from './getVideosVersioningComposeSchema';
import getVideosVersioningPropertiesSchema from './getVideosVersioningPropertiesSchema';

export default ({
  adminSettings,
  currentUser,
  isHomePage,
  isTopicsPage,
  id,
  type,
  videoVersion,
  videoProperties,
  videoPropertiesKey,
  videoCompose,
}) => {
  const tabs = [];
  const isInLiveMode = type === 'live';
  const {
    is_shortcut: isShortcut,
    video_id: courseId,
    is_child_locale: isChildLocale,
  } = videoVersion.data || {};
  const insightsEnabled =
    currentUser.data.authority === 'superadmin' ||
    currentUser.data.roles?.dashboard === 'admin' ||
    currentUser.data.roles?.dashboard === 'admin_all';

  if (!isHomePage && isTopicsPage) {
    tabs.push(
      {
        id: 'properties',
        title: 'Properties',
        body: <VideosAdminTopicPropertiesContainer />,
      },
      {
        id: 'topic-permissions',
        title: 'Permissions',
        body: <VideosAdminPermissionsContainer isTopicPermissions />,
      },
    );
  }

  if (isHomePage) {
    tabs.push(
      {
        id: 'on-demand',
        title: i18n.t('on-demand-videos'),
        body: <VideosAdminContentContainer isTopicsPage={isTopicsPage} />,
      },
      {
        id: 'live',
        title: i18n.t('live-videos'),
        body: <VideosAdminContentContainer />,
      },
    );
  }
  if (!isTopicsPage && !isHomePage) {
    tabs.push(
      ...(isShortcut
        ? [
            {
              id: 'info',
              title: i18n.t('info'),
              body: <VideosAdminInfo videoCompose={videoCompose} />,
            },
          ]
        : [
            {
              id: 'compose',
              title: 'Compose',
              body: <VideosAdminComposeContainer />,
            },
            {
              id: 'properties',
              title: 'Properties',
              body: <VideosAdminPropertiesContainer />,
            },
          ]),
      ...(isInLiveMode
        ? [
            {
              id: 'enrollments',
              title: 'Enrollments',
              body:
                currentUser.data.authority === 'superadmin' ? (
                  <Enrollments
                    {...getVideoAdminEnrollmentsOptions({ videoCompose, videoVersion })}
                  />
                ) : (
                  <VideosUnauthorizedContainer hasBreadcrumbs={false} />
                ),
            },
          ]
        : []),
      ...(!isInLiveMode
        ? [
            {
              id: 'views',
              title: 'Views',
              body: <VideosAdminViews />,
            },
          ]
        : []),
      ...(!isChildLocale
        ? [
            {
              id: 'video-permissions',
              title: 'Permissions',
              body: <VideosAdminPermissionsContainer isTopicPermissions={false} />,
            },
          ]
        : []),
      ...(!isShortcut
        ? [
            {
              id: 'versioning',
              title: i18n.t('versions'),
              body: (
                <VersioningList
                  getPropertiesAxiosConfig={(data) =>
                    getAxiosConfig('get-video-properties', {
                      ...data,
                      isInLiveMode,
                      versionId: data.id,
                      videoId: id,
                    })
                  }
                  getVersionAxiosConfig={(data) =>
                    getAxiosConfig('version-video', {
                      ...data,
                      isInLiveMode,
                      videoId: id,
                    })
                  }
                  onVersionContent={() => videoVersion.refetch()}
                  getComposeSchema={(model) =>
                    getVideosVersioningComposeSchema({ id: model.id, isInLiveMode })
                  }
                  getPropertiesSchema={() =>
                    getVideosVersioningPropertiesSchema({
                      adminSettings,
                      id,
                      isInLiveMode,
                      videoProperties,
                      videoPropertiesKey,
                    })
                  }
                  queryParamsToPersist={['version_id']}
                  versionsAxiosConfig={getAxiosConfig('get-video-versions', {
                    id,
                    isInLiveMode,
                    versionId: urlSearch.getValue('version_id'),
                  })}
                />
              ),
            },
            ...(!isChildLocale && insightsEnabled
              ? [
                  {
                    id: 'insights',
                    ...(adminSettings.data?.enabled_features?.includes('enable_dashboards')
                      ? {
                          title: <InsightsTab id={courseId} />,
                        }
                      : {
                          title: i18n.t('insights'),
                          body: <InsightsDashboardsContainer id={courseId} />,
                        }),
                  },
                ]
              : []),
          ]
        : []),
    );
  }
  return tabs;
};
